import type { CalcKey } from './keys';

export const nl: Record<CalcKey, string> = {
  // Common controls
  'panel.params': 'Parameters',
  'panel.advanced': 'Geavanceerd',
  'action.reset': 'Opnieuw',
  'action.randomise': 'Willekeurig',
  'action.reset-view': 'Weergave herstellen',
  // App chrome
  'app.eyebrow': 'Wiskunde · Calculus',
  // Experiment selector
  'panel.experiment': 'Experiment',
  // Experiment labels
  'experiment.limit-lab': 'Limietlab',
  'experiment.tangent-lab': 'Raaklijnlab',
  'experiment.derivative-plotter': 'Afgeleide plotter',
  'experiment.applications': 'Toepassingen',
  'experiment.riemann-lab': 'Riemann lab',
  'experiment.ftc-lab': 'HWS lab',
  'experiment.taylor-lab': 'Taylor lab',
  'experiment.slope-field-lab': 'Richtingsveld lab',
  // Limit presets
  'preset.limit.removable': 'Verwijderbare discontinuïteit',
  'preset.limit.jump': 'Sprongdiscontinuïteit',
  'preset.limit.infinite': 'Oneindige discontinuïteit',
  'preset.limit.oscillating': 'Oscillerende discontinuïteit',
  'preset.limit.continuous': 'Continu (controle)',
  // Tangent presets
  'preset.tangent.parabola': 'Parabool x²',
  'preset.tangent.sine': 'Sinus sin(x)',
  'preset.tangent.cubic': 'Derdemacht x³ − 3x',
  'preset.tangent.exponential': 'Exponentieel eˣ',
  // Limit-lab panel labels
  'limit.panel.preset': 'Functie',
  'limit.panel.approach': 'Naderen x',
  'limit.panel.epsilon': 'ε (epsilon)',
  'limit.panel.show-epsilon-delta': 'Toon ε–δ band',
  // Limit-lab readout labels
  'limit.readout.left': 'lim x→a⁻',
  'limit.readout.right': 'lim x→a⁺',
  'limit.readout.fa': 'f(a)',
  'limit.readout.kind': 'soort',
  // Limit-lab info / help
  'experiment.limit-lab.info':
    'Sleep de cursor langs de x-as om de limiet te peilen. De weergave toont de eenzijdige limieten en classificeert de discontinuïteit bij het naderingspunt.',
  // Tangent-lab panel labels
  'tangent.panel.preset': 'Functie',
  'tangent.panel.a': 'Punt a',
  'tangent.panel.h': 'Stap h',
  'tangent.panel.mode': 'Quotiënttype',
  // Tangent-lab mode values
  'tangent.mode.forward': 'Voorwaarts',
  'tangent.mode.symmetric': 'Symmetrisch',
  // Tangent-lab info / help
  'experiment.tangent-lab.info':
    'Sleep P om het basispunt te verplaatsen en Q om de stap h te wijzigen. Zie hoe de secant de raaklijn nadert naarmate h → 0.',
  // Derivative presets
  'preset.derivative.cubic': 'Derdemacht x³ − 3x',
  'preset.derivative.sine': 'Sinus sin(x)',
  'preset.derivative.parabola': 'Parabool x²',
  'preset.derivative.bell': 'Klokcurve e^(−x²)',
  // Application presets
  'preset.application.open-box': 'Open doos volume',
  'preset.application.rect-under-line': 'Rechthoek onder een lijn',
  'preset.application.ladder': 'Glijdende ladder',
  'preset.application.ripple': 'Uitdijende kring',
  // Derivative-plotter panel labels
  'derivative.panel.preset': 'Functie',
  'derivative.panel.cursor-x': 'Cursor x',
  // Derivative-plotter info
  'experiment.derivative-plotter.info':
    'Sleep de cursor langs de x-as. Het bovenste venster toont f met een raaklijnsegment; het onderste venster traceert f′ — het verbindingspunt toont hoe de helling van de raaklijn gelijk is aan de afgeleide waarde. Geavanceerd voegt f″ en concaviteitsarcering toe.',
  // Applications panel labels
  'applications.panel.preset': 'Scenario',
  'applications.panel.param': 'Parameter',
  'applications.panel.mode': 'Modus',
  // Applications mode values
  'applications.mode.optimization': 'Optimalisatie',
  'applications.mode.related-rates': 'Gerelateerde snelheden',
  // Applications info
  'experiment.applications.info':
    'Optimalisatie: sleep de parameter om het geometrisch scenario en de kwantiteitscurve te zien — de helling van de markering wordt vlak bij het optimum. Gerelateerde snelheden: zie hoe de vectoren dx/dt en dy/dt samenhangen via de kettingregel.',
  // Integral presets
  'preset.integral.polynomial': 'Veelterm x²',
  'preset.integral.sine': 'Sinus sin(x)',
  'preset.integral.cosine': 'Cosinus cos(x)',
  'preset.integral.exponential': 'Exponentieel eˣ',
  'preset.integral.linear': 'Lineair 2x + 1',
  'preset.integral.reciprocal': 'Reciprook 1/x',
  // Riemann-lab panel labels
  'riemann.panel.preset': 'Functie',
  'riemann.panel.n': 'Deelintervallen n',
  'riemann.panel.rule': 'Regel',
  // Riemann-lab rule values
  'riemann.rule.left': 'Links',
  'riemann.rule.right': 'Rechts',
  'riemann.rule.mid': 'Middelpunt',
  'riemann.rule.trap': 'Trapezium',
  // Riemann-lab info / help
  'experiment.riemann-lab.info':
    'Pas n aan om [a, b] in meer rechthoeken te verdelen en zie hoe de Riemannsom Σ convergeert naar de exacte integraal ∫. De weergave toont de getekende som, de waarde van de curated primitieve en de fout |Σ − ∫|.',
  // FTC-lab panel labels
  'ftc.panel.preset': 'Functie',
  'ftc.panel.x': 'Bovengrens x',
  'ftc.panel.show-second-ftc': 'Toon 2e HDW (numerieke check)',
  // FTC-lab info / help
  'experiment.ftc-lab.info':
    'Sleep x om oppervlakte onder f te accumuleren van a tot x. Het bovenste venster arcreert de oppervlakte ∫ₐˣ f; het onderste venster traceert A(x) = F(x) − F(a) met een verbindingspunt. De weergave toont de curated primitieve F en de HDW-identiteit A′(x) = f(x).',
  // Taylor presets
  'preset.taylor.exp': 'Exponentieel eˣ',
  'preset.taylor.sin': 'Sinus sin(x)',
  'preset.taylor.cos': 'Cosinus cos(x)',
  'preset.taylor.ln1px': 'Logaritme ln(1+x)',
  'preset.taylor.geometric': 'Meetkundige 1/(1−x)',
  'preset.taylor.atan': 'Arctangens atan(x)',
  // Taylor-lab panel labels
  'taylor.panel.preset': 'Functie',
  'taylor.panel.degree': 'Graad n',
  'taylor.panel.center': 'Middelpunt a',
  // Taylor-lab info / help
  'experiment.taylor-lab.info':
    'Sleep het middelpunt langs de x-as om het ontwikkelingscentrum te verplaatsen. Verhoog de graadschuifregelaar om meer termen toe te voegen — zie hoe Pₙ(x) f benadert nabij het centrum en divergeert buiten het gearceerde convergentie-interval. De weergave toont het exacte polynoom en de coëfficiënten.',
  // Slope-field presets
  'preset.slope-field.exponential-growth': 'Exponentiële groei y′ = y',
  'preset.slope-field.linear-x': 'Lineaire helling y′ = x',
  'preset.slope-field.circles': 'Concentrische cirkels y′ = −x/y',
  'preset.slope-field.linear-ode': 'Lineaire ODE y′ = x − y',
  // Slope-field-lab panel labels
  'slope-field.panel.preset': 'Vergelijking dy/dx',
  'slope-field.panel.step-h': 'Stap h',
  'slope-field.panel.show-family': 'Toon oplossingsfamilie',
  // Slope-field-lab info / help
  'experiment.slope-field-lab.info':
    'Sleep het beginvoorwaardepunt om een oplossingscurve door het richtingsveld te trekken. In geavanceerde modus verklein je h om de Euler-fout te verkleinen en de familie van oplossingscurven te zien. De vage overlay toont de gesloten-vorm oplossing waar beschikbaar.',
};
